import { useI18n } from '../../i18n';
import type { Bout, BoutVerdict } from '../../types';
import { cornerName } from '../../utils/personName';

/** The result card of a scored bout: the winner banner in the colour of the
 *  corner, the outcome code and the round of a stop, and the two final scores
 *  of a WP. The bout page renders it for a bout with status 'scored'. The
 *  attributes `data-verdict-winner` and `data-verdict-outcome` on the root
 *  are the anchors of the e2e specs (#661, F12). */
export default function VerdictResult({ bout, verdict: v }: { bout: Bout; verdict: BoutVerdict }) {
  const { t } = useI18n();

  const isRed  = v.winner === 'red';
  const isBlue = v.winner === 'blue';
  const winnerName  = isRed ? cornerName(bout, 'red') : isBlue ? cornerName(bout, 'blue') : '';
  const winnerTeam  = isRed ? bout.red_team_name   : isBlue ? bout.blue_team_name  : null;
  const showScores = v.outcome === 'WP' &&
    (v.red_corner_score != null || v.blue_corner_score != null);
  const bannerCls = isRed
    ? 'bg-red-50 text-red-900 border-b border-red-100'
    : isBlue
    ? 'bg-blue-50 text-blue-900 border-b border-blue-100'
    : 'bg-gray-50 text-gray-800 border-b border-gray-100';

  return (
    <div className="card overflow-hidden" data-verdict-winner={v.winner ?? 'draw'} data-verdict-outcome={v.outcome ?? ''}>
      <div className={`px-4 py-3 ${bannerCls}`}>
        <div className={`text-xs font-semibold uppercase tracking-widest mb-1 ${isRed ? 'text-red-400' : isBlue ? 'text-blue-400' : 'text-gray-400'}`}>{t.common.winner}</div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            {winnerName ? (
              <span className="font-black text-lg leading-tight">{winnerName}</span>
            ) : (
              <span className="font-black text-lg">DRAW</span>
            )}
          </div>
          <div className="flex flex-col items-end shrink-0 gap-0.5">
            <span className="px-2.5 py-0.5 rounded-full text-sm font-black bg-gray-200 text-gray-800">{v.outcome}</span>
            {v.round_ended && (
              <span className="text-xs opacity-50">{t.common.round} {v.round_ended}</span>
            )}
          </div>
        </div>
        {winnerTeam && <div className={`text-sm mt-0.5 ${isRed ? 'text-red-500' : isBlue ? 'text-blue-500' : 'text-gray-500'}`}>{winnerTeam}</div>}
      </div>
      {showScores && (
        <div className="flex justify-center gap-8 px-4 py-3">
          <div className="text-center">
            <div className={`font-black font-mono text-2xl ${isRed ? 'text-red-600' : 'text-gray-400'}`}>
              {v.red_corner_score ?? '—'}
            </div>
          </div>
          <div className="text-center">
            <div className={`font-black font-mono text-2xl ${isBlue ? 'text-blue-600' : 'text-gray-400'}`}>
              {v.blue_corner_score ?? '—'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
